import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Loader2, ArrowRight, TrendingUp, TrendingDown, Minus, Plus, X } from "lucide-react";
import { Ticker } from "@/components/TickerDrawer";
import {
  buildWhatChangedSentences,
  RECOUNT_TOOLTIP,
} from "@/components/compareScansSummary";

const CLASSIFICATION_STYLE = {
  UPGRADED:               { tone: "text-signal-green border-signal-green/40 bg-signal-green/10",       icon: TrendingUp, label: "UPGRADED" },
  TRUE_NEW:               { tone: "text-[#0A84FF] border-[#0A84FF]/40 bg-[#0A84FF]/10",                  icon: Plus,       label: "TRUE NEW" },
  NEWLY_SCANNED:          { tone: "text-[#0A84FF] border-[#0A84FF]/40 bg-[#0A84FF]/5",                    icon: Plus,       label: "NEWLY SCANNED" },
  NEW_EVALUATION_UNKNOWN: { tone: "text-[#0A84FF] border-dashed border-[#0A84FF]/40 bg-[#0A84FF]/5",      icon: Plus,       label: "NEW · UNKNOWN" },
  DOWNGRADED:             { tone: "text-signal-yellow border-signal-yellow/40 bg-signal-yellow/10",     icon: TrendingDown, label: "DOWNGRADED" },
  TRUE_DROPPED:           { tone: "text-signal-red border-signal-red/40 bg-signal-red/10",              icon: X,          label: "TRUE DROPPED" },
  NOT_RESCANNED:          { tone: "text-signal-yellow border-signal-yellow/40 bg-signal-yellow/5",     icon: X,          label: "NOT RE-SCANNED" },
  STATUS_UNKNOWN:         { tone: "text-signal-yellow border-dashed border-signal-yellow/40 bg-signal-yellow/5", icon: X,   label: "DROPPED · UNKNOWN" },
  UNCHANGED:              { tone: "text-muted-foreground border-border/60 bg-muted/10",                 icon: Minus,      label: "UNCHANGED" },
};

const CLASSIFICATION_ORDER = [
  "UPGRADED", "TRUE_NEW", "NEW_EVALUATION_UNKNOWN", "NEWLY_SCANNED",
  "DOWNGRADED", "TRUE_DROPPED", "STATUS_UNKNOWN", "NOT_RESCANNED",
  "UNCHANGED",
];

const CLASSIFICATION_TOOLTIP = {
  UPGRADED:                "Recommended in both scans with a stronger verdict in Scan B.",
  DOWNGRADED:              "Recommended in both scans with a weaker verdict in Scan B.",
  UNCHANGED:               "Recommended in both scans with the same verdict (score changes alone do not upgrade or downgrade).",
  TRUE_NEW:                "Evaluated in both scans; only recommended in Scan B. Persisted evidence proves the symbol was considered in Scan A and not recommended.",
  TRUE_DROPPED:            "Recommended in Scan A. Persisted evidence proves the symbol was evaluated in Scan B but no longer recommended.",
  NEWLY_SCANNED:           "Recommended in Scan B. Persisted evidence proves the symbol was NOT evaluated in Scan A — it entered the universe between scans.",
  NOT_RESCANNED:           "Recommended in Scan A. Persisted evidence proves the symbol was NOT evaluated in Scan B — this is not evidence that WingWise rejected the stock.",
  NEW_EVALUATION_UNKNOWN:  "Recommended in Scan B. Historical evidence is insufficient to determine whether Scan A evaluated this symbol.",
  STATUS_UNKNOWN:          "Recommended in Scan A. Historical evidence is insufficient to determine whether Scan B evaluated this symbol.",
};

function fmtETLong(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      timeZone: "America/New_York", month: "short", day: "numeric",
      year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true,
    }) + " ET";
  } catch { return iso; }
}

function fmtNum(v, digits = 2) {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toFixed(digits);
}

function DeltaChip({ label, value, positiveIsGood = true, tid }) {
  if (value === null || value === undefined) return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  const sign = n > 0 ? "+" : "";
  const isPositive = n > 0;
  const isNeutral = n === 0;
  let tone = "text-muted-foreground border-border/60";
  if (!isNeutral) {
    const good = positiveIsGood ? isPositive : !isPositive;
    tone = good ? "text-signal-green border-signal-green/40"
                : "text-signal-red border-signal-red/40";
  }
  return (
    <span className={`chip !text-[9px] mono ${tone}`} data-testid={tid}>
      {label} {sign}{fmtNum(n, Math.abs(n) < 1 && n !== 0 ? 4 : 2)}
    </span>
  );
}

function ScanHeaderCard({ scan, label, tid }) {
  if (!scan) return null;
  const badgeTone =
    scan.evidence_type === "INDEPENDENT"
      ? "text-signal-green border-signal-green/40"
      : scan.evidence_type === "VERIFICATION" || scan.evidence_type === "DUPLICATE_SESSION"
        ? "text-signal-red border-signal-red/40"
        : "text-signal-yellow border-signal-yellow/40";
  return (
    <div className="grid-cell !p-3" data-testid={tid}>
      <div className="flex items-center justify-between mb-1">
        <span className="label-cap">{label}</span>
        <span className={`chip !text-[9px] ${badgeTone}`}
              data-testid={`${tid}-evidence`}>
          {(scan.evidence_type || "UNKNOWN").replace(/_/g, " ")}
        </span>
      </div>
      <div className="mono text-sm text-foreground" data-testid={`${tid}-session`}>
        {scan.market_session || "—"}
      </div>
      <div className="mono text-[11px] text-muted-foreground" data-testid={`${tid}-time`}>
        {fmtETLong(scan.started_at)}
      </div>
      <div className="mono text-[11px] mt-1" data-testid={`${tid}-count`}>
        <span className="text-foreground">{scan.recommendations_generated ?? 0}</span>{" "}
        <span className="text-muted-foreground">recommendation{(scan.recommendations_generated ?? 0) === 1 ? "" : "s"}</span>
      </div>
      <div className={`text-[10px] mono mt-1 ${scan.reconciled ? "text-signal-green" : "text-signal-yellow"}`}
           data-testid={`${tid}-integrity`}>
        {scan.recommendations_generated ?? 0} generated · {scan.snapshot_records ?? 0} snapshots ·{" "}
        <span className={scan.reconciled ? "" : "text-red-400"}>
          {scan.reconciled ? "RECONCILED" : "MISMATCH"}
        </span>
      </div>
      {scan.valid_zero_recommendation_scan && (
        <div className="text-[10px] mono text-muted-foreground mt-1">
          Valid zero-recommendation scan
        </div>
      )}
    </div>
  );
}

// Re-export the pure helpers on the JSX module so tests / callers can
// import them from either path.
export { buildWhatChangedSentences, RECOUNT_TOOLTIP as __RECOUNT_TOOLTIP__ };

function ClassificationLegend() {
  const items = [
    ["TRUE NEW",       "Evaluated in both scans; became a recommendation in Scan B."],
    ["TRUE DROPPED",   "Evaluated in both scans; recommendation disappeared in Scan B."],
    ["NEWLY SCANNED",  "Not evaluated in Scan A; evaluated and recommended in Scan B."],
    ["NOT RE-SCANNED", "Recommended in Scan A but not evaluated in Scan B — not a rejection."],
    ["UPGRADED",       "Evaluated in both scans; persisted verdict strengthened."],
    ["DOWNGRADED",     "Evaluated in both scans; persisted verdict weakened."],
    ["UNCHANGED",      "Evaluated in both scans; persisted verdict classification stayed the same."],
    ["UNKNOWN",        "Persisted evidence is insufficient to classify safely."],
  ];
  return (
    <div className="mt-2 text-[10px] mono border-t border-border/40 pt-2 space-y-1"
         data-testid="compare-classification-legend">
      {items.map(([k, v]) => (
        <div key={k} className="grid grid-cols-[110px_1fr] gap-2">
          <span className="text-foreground">{k}</span>
          <span className="text-muted-foreground">{v}</span>
        </div>
      ))}
    </div>
  );
}

function SummaryStrip({ summary, integrity, evaluationIntegrity }) {
  const [showLegend, setShowLegend] = useState(false);
  const items = [
    { label: "SCAN A", value: summary.scan_a_count, tid: "compare-summary-a" },
    { label: "SCAN B", value: summary.scan_b_count, tid: "compare-summary-b" },
    { label: "IN BOTH", value: summary.symbols_in_both, tid: "compare-summary-both" },
    { label: "UPGRADED", value: summary.upgraded, tid: "compare-summary-upgraded" },
    { label: "DOWNGRADED", value: summary.downgraded, tid: "compare-summary-downgraded" },
    { label: "UNCHANGED", value: summary.unchanged, tid: "compare-summary-unchanged" },
    { label: "TRUE NEW", value: summary.true_new, tid: "compare-summary-true-new" },
    { label: "TRUE DROP", value: summary.true_dropped, tid: "compare-summary-true-dropped" },
    { label: "NEWLY SCAN", value: summary.newly_scanned, tid: "compare-summary-newly-scanned" },
    { label: "NOT RE-SCAN", value: summary.not_rescanned, tid: "compare-summary-not-rescanned" },
    { label: "UNKNOWN", value: summary.unknown_total, tid: "compare-summary-unknown" },
    { label: "REC COUNT Δ",
       value: (summary.net_recommendation_change > 0 ? "+" : "")
              + summary.net_recommendation_change,
       tid: "compare-summary-net",
       tooltip: RECOUNT_TOOLTIP },
  ];
  const sentences = buildWhatChangedSentences(summary);
  return (
    <div className="grid-cell !p-3" data-testid="compare-summary-strip">
      <div className="flex items-center justify-between mb-2">
        <div className="label-cap">Summary</div>
        <button
          type="button"
          onClick={() => setShowLegend((v) => !v)}
          className="text-[10px] mono text-[#0A84FF] hover:underline"
          aria-label="Toggle classification legend"
          data-testid="compare-legend-toggle">
          {showLegend ? "− Hide definitions" : "? What do these mean?"}
        </button>
      </div>
      <div className="grid grid-cols-4 md:grid-cols-6 gap-px bg-border/40">
        {items.map((it) => (
          <div key={it.label} className="p-2 bg-card/40" title={it.tooltip || ""}
               data-testid={it.tid}>
            <div className="label-cap">{it.label}</div>
            <div className="mono text-lg text-foreground">{it.value ?? 0}</div>
          </div>
        ))}
      </div>
      <div className="mt-2 text-[10px] mono text-muted-foreground italic"
           data-testid="compare-universe-note">
        Scan universes may differ between sessions. Classifications above
        distinguish evidence (evaluated in both scans) from inference
        (evaluated in only one).
      </div>
      {sentences.length > 0 && (
        <div className="mt-3 border-t border-border/40 pt-2"
             data-testid="compare-what-changed">
          <div className="label-cap mb-1">What changed?</div>
          <ul className="text-[11px] mono text-foreground list-disc list-inside space-y-1">
            {sentences.map((s, i) => (
              <li key={i} data-testid={`compare-what-changed-item-${i}`}>{s}</li>
            ))}
          </ul>
        </div>
      )}
      {showLegend && <ClassificationLegend />}
      {evaluationIntegrity?.historical_lineage_note && (
        <div className="mt-2 border border-signal-yellow/40 bg-signal-yellow/5 p-2 text-[10px] mono text-signal-yellow"
             data-testid="compare-evaluation-lineage-note">
          ⚠ {evaluationIntegrity.historical_lineage_note}
        </div>
      )}
      {!integrity.comparison_reconciled && (
        <div className="mt-2 border border-signal-red/40 bg-signal-red/5 p-2 text-[10px] mono text-signal-red"
             data-testid="compare-integrity-warning">
          ⚠ {integrity.note || "Comparison integrity mismatch"}
        </div>
      )}
    </div>
  );
}

function VerdictBadge({ verdict, tid }) {
  if (!verdict) return <span className="mono text-[11px] text-muted-foreground" data-testid={tid}>—</span>;
  return (
    <span className="mono text-[11px] text-foreground" data-testid={tid}>{verdict.replace(/_/g, " ")}</span>
  );
}

function CompareRow({ row, onOpen }) {
  const cls = CLASSIFICATION_STYLE[row.classification] || CLASSIFICATION_STYLE.UNCHANGED;
  const Icon = cls.icon;
  const nameLine = row.name && row.name.toUpperCase() !== row.symbol.toUpperCase()
    ? row.name : "";
  const tooltip = CLASSIFICATION_TOOLTIP[row.classification] || "";
  const evalStateA = row.scan_a?.evaluation_state || "UNKNOWN";
  const evalStateB = row.scan_b?.evaluation_state || "UNKNOWN";
  const humanEval = (s) => (s || "").replace(/_/g, " ");
  return (
    <div className="grid-cell !p-3 hover:bg-card/60 transition-colors cursor-pointer"
         onClick={() => onOpen && onOpen(row.symbol)}
         data-testid={`compare-row-${row.symbol}`}>
      <div className="flex items-start gap-3">
        <div className={`chip !text-[10px] mono ${cls.tone} flex items-center gap-1`}
             title={tooltip}
             data-testid={`compare-row-${row.symbol}-cls`}>
          <Icon size={11} />{cls.label}
          {row.classification_confidence === "UNKNOWN" && (
            <span className="ml-1 text-muted-foreground">?</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            <div className="mono text-base font-medium text-foreground">
              <Ticker symbol={row.symbol} size="sm"/>
            </div>
            <div className="mono text-[10px] text-muted-foreground truncate">
              {nameLine}
            </div>
          </div>
          <div className="mt-1 grid grid-cols-2 gap-3 text-[10px] mono">
            <div data-testid={`compare-row-${row.symbol}-eval-a`}>
              <span className="label-cap">Scan A:</span>
              <span className="ml-1 text-foreground">{humanEval(evalStateA)}</span>
            </div>
            <div data-testid={`compare-row-${row.symbol}-eval-b`}>
              <span className="label-cap">Scan B:</span>
              <span className="ml-1 text-foreground">{humanEval(evalStateB)}</span>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 mt-2 text-[11px] mono">
            <div>
              <div className="label-cap">Verdict</div>
              <div className="flex items-center gap-1 text-foreground">
                <VerdictBadge verdict={row.a?.verdict} tid={`compare-row-${row.symbol}-verdict-a`} />
                <ArrowRight size={10} className="text-muted-foreground"/>
                <VerdictBadge verdict={row.b?.verdict} tid={`compare-row-${row.symbol}-verdict-b`} />
              </div>
            </div>
            <div>
              <div className="label-cap">Score</div>
              <div className="text-foreground" data-testid={`compare-row-${row.symbol}-score`}>
                {row.a?.score ?? "—"} → {row.b?.score ?? "—"}
              </div>
            </div>
            <div>
              <div className="label-cap">Confidence</div>
              <div className="text-foreground" data-testid={`compare-row-${row.symbol}-conf`}>
                {row.a?.confidence ?? "—"} → {row.b?.confidence ?? "—"}
              </div>
            </div>
            <div>
              <div className="label-cap">Risk</div>
              <div className="text-foreground" data-testid={`compare-row-${row.symbol}-risk`}>
                {row.a?.risk_score ?? "—"} → {row.b?.risk_score ?? "—"}
              </div>
            </div>
            <div>
              <div className="label-cap">Portfolio</div>
              <div className="text-muted-foreground text-[10px]">
                {row.portfolio_bucket_a || "—"} → {row.portfolio_bucket_b || "—"}
              </div>
            </div>
            <div>
              <div className="label-cap">Historical Price</div>
              <div className="text-muted-foreground text-[10px]" data-testid={`compare-row-${row.symbol}-price`}>
                {row.a?.market_price ? `$${fmtNum(row.a.market_price)}` : "—"}{" → "}
                {row.b?.market_price ? `$${fmtNum(row.b.market_price)}` : "—"}
              </div>
            </div>
            <div>
              <div className="label-cap">Entry</div>
              <div className="text-muted-foreground text-[10px]">
                {row.a?.suggested_entry ? `$${fmtNum(row.a.suggested_entry)}` : "—"}{" → "}
                {row.b?.suggested_entry ? `$${fmtNum(row.b.suggested_entry)}` : "—"}
              </div>
            </div>
            <div>
              <div className="label-cap">Stop</div>
              <div className="text-muted-foreground text-[10px]">
                {row.a?.stop ? `$${fmtNum(row.a.stop)}` : "—"}{" → "}
                {row.b?.stop ? `$${fmtNum(row.b.stop)}` : "—"}
              </div>
            </div>
            <div>
              <div className="label-cap">Target</div>
              <div className="text-muted-foreground text-[10px]">
                {row.a?.target ? `$${fmtNum(row.a.target)}` : "—"}{" → "}
                {row.b?.target ? `$${fmtNum(row.b.target)}` : "—"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-wrap mt-2">
            <DeltaChip label="Score" value={row.deltas?.score} tid={`compare-row-${row.symbol}-score-delta`} />
            <DeltaChip label="Conf" value={row.deltas?.confidence} tid={`compare-row-${row.symbol}-conf-delta`} />
            <DeltaChip label="Risk" value={row.deltas?.risk_score} positiveIsGood={false}
                       tid={`compare-row-${row.symbol}-risk-delta`} />
            <DeltaChip label="Δ Target" value={row.deltas?.target}
                       tid={`compare-row-${row.symbol}-target-delta`} />
          </div>
          {tooltip && (
            <div className="mt-2 text-[10px] mono text-muted-foreground italic"
                 data-testid={`compare-row-${row.symbol}-explanation`}>
              {tooltip}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * CompareScansView — WingWise Compare Sessions (P2, Feb-2026)
 *
 * Historical-vs-historical read-only comparison. All data pulled from
 * /api/opportunities/compare-scans which reads exclusively from
 * recommendation_snapshots. NO current market state, NO yfinance, NO LLM.
 */
export default function CompareScansView({
  scanList,          // scan_run rows filtered by evidence
  evidenceFilter,    // independent | diagnostic | all
  scanA, scanB,      // scan_run_id | null
  onChangeA, onChangeB,
  onExit,
  onOpen,            // (symbol) => open analysis drawer
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("ALL");
  const [bucketFilter, setBucketFilter] = useState("ALL");

  // Intelligent defaults: A = latest PREMARKET, B = latest MIDDAY same-date.
  useEffect(() => {
    if (scanA || scanB || scanList.length === 0) return;
    const premarket = scanList.filter((s) => s.market_session === "PREMARKET");
    const midday = scanList.filter((s) => s.market_session === "MIDDAY");
    if (premarket.length && midday.length) {
      const latestPre = premarket[0];
      const preDate = (latestPre.started_at || "").slice(0, 10);
      const matchingMid = midday.find((s) => (s.started_at || "").slice(0, 10) === preDate);
      if (matchingMid) {
        onChangeA(latestPre.scan_run_id);
        onChangeB(matchingMid.scan_run_id);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanList]);

  useEffect(() => {
    if (!scanA || !scanB) { setData(null); return; }
    if (scanA === scanB) { setData(null); setError("Pick two different scans"); return; }
    setLoading(true); setError(null);
    api.get("/opportunities/compare-scans", {
      params: { scan_a: scanA, scan_b: scanB },
    }).then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || e?.message || "Failed to load comparison"))
      .finally(() => setLoading(false));
  }, [scanA, scanB]);

  const visibleSymbols = useMemo(() => {
    if (!data) return [];
    let rows = data.symbols;
    if (filter !== "ALL") rows = rows.filter((r) => r.classification === filter);
    if (bucketFilter !== "ALL") {
      rows = rows.filter((r) => (r.portfolio_bucket_a === bucketFilter)
                                 || (r.portfolio_bucket_b === bucketFilter));
    }
    return rows;
  }, [data, filter, bucketFilter]);

  const renderSelector = (label, value, onChange, tid) => (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="label-cap min-w-[70px]">{label}</span>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value || null)}
        data-testid={tid}
        className="bg-transparent border border-border/60 text-[11px] mono text-foreground px-2 py-1 rounded focus:border-[#0A84FF] focus:outline-none min-w-[380px]"
      >
        <option value="">— Select scan —</option>
        {scanList.map((s) => {
          const dt = s.started_at ? new Date(s.started_at) : null;
          const dtLabel = dt ? dt.toLocaleString("en-US", {
            timeZone: "America/New_York", month: "short", day: "numeric",
            hour: "2-digit", minute: "2-digit", hour12: true,
          }) + " ET" : "—";
          return (
            <option key={s.scan_run_id} value={s.scan_run_id}>
              {s.market_session} · {dtLabel} · {s.recommendation_count ?? 0} recs · {s.evidence_classification}
            </option>
          );
        })}
      </select>
    </div>
  );

  return (
    <div className="space-y-3" data-testid="compare-scans-view">
      <div className="grid-cell !p-3" data-testid="compare-scan-selectors">
        <div className="flex items-center justify-between mb-2">
          <div className="label-cap">Compare Sessions · <span className="text-muted-foreground">{evidenceFilter.toUpperCase()}</span></div>
          <button className="text-[11px] mono text-[#0A84FF] hover:underline"
                  onClick={onExit} data-testid="compare-exit">
            ← Back to single scan
          </button>
        </div>
        <div className="space-y-2">
          {renderSelector("SCAN A", scanA, onChangeA, "compare-select-a")}
          {renderSelector("SCAN B", scanB, onChangeB, "compare-select-b")}
        </div>
      </div>

      {loading && (
        <div className="grid-cell !p-6 flex items-center justify-center text-muted-foreground"
             data-testid="compare-loading">
          <Loader2 className="animate-spin mr-2" size={16}/> Loading comparison…
        </div>
      )}
      {error && (
        <div className="grid-cell !p-3 text-signal-yellow mono text-[11px]"
             data-testid="compare-error">
          {error}
        </div>
      )}
      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <ScanHeaderCard scan={data.scan_a} label="SCAN A" tid="compare-scan-a" />
            <ScanHeaderCard scan={data.scan_b} label="SCAN B" tid="compare-scan-b" />
          </div>
          <SummaryStrip
            summary={data.summary}
            integrity={data.integrity}
            evaluationIntegrity={data.evaluation_integrity}
          />

          {data.scan_a?.recommendations_generated === 0 && (
            <div className="grid-cell !p-3 border border-signal-yellow/40 bg-signal-yellow/5 text-[11px] mono text-signal-yellow"
                 data-testid="compare-zero-scan-banner">
              {data.scan_a.market_session} produced 0 recommendations. Classification below distinguishes stocks that were evaluated and rejected from stocks that were not evaluated, where historical evidence is available.
            </div>
          )}

          <div className="flex items-center gap-2 flex-wrap" data-testid="compare-filter-chips">
            <span className="label-cap">Filter</span>
            {[
              ["ALL",                    data.symbols.length],
              ["UPGRADED",               data.summary.upgraded],
              ["TRUE_NEW",               data.summary.true_new],
              ["NEWLY_SCANNED",          data.summary.newly_scanned],
              ["NEW_EVALUATION_UNKNOWN", data.summary.new_evaluation_unknown],
              ["DOWNGRADED",             data.summary.downgraded],
              ["TRUE_DROPPED",           data.summary.true_dropped],
              ["NOT_RESCANNED",          data.summary.not_rescanned],
              ["STATUS_UNKNOWN",         data.summary.status_unknown],
              ["UNCHANGED",              data.summary.unchanged],
            ].map(([f, count]) => {
              const label = f === "ALL" ? "ALL"
                : (CLASSIFICATION_STYLE[f]?.label || f.replace(/_/g, " "));
              return (
                <button key={f} onClick={() => setFilter(f)}
                        title={CLASSIFICATION_TOOLTIP[f] || ""}
                        className={`chip cursor-pointer ${filter === f ? "border-[#0A84FF] text-[#0A84FF]" : "border-border/60 text-muted-foreground hover:text-foreground"}`}
                        data-testid={`compare-filter-${f.toLowerCase()}`}>
                  {label} <span className="opacity-60 ml-1">{count ?? 0}</span>
                </button>
              );
            })}
            <span className="mx-2 text-border">|</span>
            <span className="label-cap">Bucket</span>
            {["ALL", "LONG_TERM", "SWING", "TACTICAL"].map((b) => (
              <button key={b} onClick={() => setBucketFilter(b)}
                      className={`chip cursor-pointer ${bucketFilter === b ? "border-[#0A84FF] text-[#0A84FF]" : "border-border/60 text-muted-foreground hover:text-foreground"}`}
                      data-testid={`compare-bucket-${b.toLowerCase()}`}>
                {b.replace(/_/g, " ")}
              </button>
            ))}
          </div>

          {visibleSymbols.length === 0 ? (
            <div className="grid-cell !p-6 text-center text-muted-foreground mono text-[11px]"
                 data-testid="compare-empty">
              {data.symbols.length === 0
                ? "Both scans produced zero comparable recommendations."
                : "No symbols match the current filter."}
            </div>
          ) : (
            <div className="space-y-2" data-testid="compare-symbols">
              {visibleSymbols.map((row) => (
                <CompareRow key={row.symbol} row={row} onOpen={onOpen} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
